<template>
  <form @submit.prevent="submit">
    <b-row>
      <b-col sm>
        <b-card :title="$t('general.general')">
          <div class="col-sm-8 offset-sm-4">
            <div class="form-check">
              <input
                :id="`ride_types_${rideType}`"
                v-model="model.ride_types"
                :value="rideType"
                type="checkbox"
                class="form-check-input"
              />
              <label :for="`ride_types_${rideType}`">
                {{ $t('vehicles.is_available') }}
              </label>
            </div>
          </div>

          <fields ungrouped />
        </b-card>
        <b-card :title="$t('vehicles.field_groups.standard')">
          <fields :group="`${fieldsGroupPrefix}standard`" />
        </b-card>
      </b-col>
      <b-col sm>
        <b-card
          v-if="rideType === 'delivery'"
          :title="$t('vehicles.field_groups.vehicle_attributes')"
        >
          <fields :group="`attributes`" />
        </b-card>
        <b-card v-else :title="$t('vehicles.field_groups.capacity')">
          <fields :group="`${rideType}_capacity`" />
        </b-card>

        <b-card :title="$t('vehicles.field_groups.additional')">
          <fields :group="`${fieldsGroupPrefix}additional`" />
        </b-card>
        <b-card
          v-if="model.pricing"
          :title="$t('vehicles.field_groups.day_night')"
        >
          <fields :group="`${fieldsGroupPrefix}day_night`" />
          <div class="form-row mt-1">
            <div class="col-4">&nbsp;</div>
            <div class="form-group col-4">{{ $t('general.day') }}</div>
            <div class="form-group col-4">{{ $t('general.night') }}</div>
          </div>
          <div v-for="(day, i) in days" :key="day" class="form-row">
            <div class="col-4 text-right pt-1">
              {{
                $moment()
                  .day(i + 1)
                  .format('dddd')
              }}
            </div>
            <div class="form-group col-4">
              <field
                :name="`${fieldsGroupPrefix}pricing.days.${day}.day`"
                layout="empty"
              />
            </div>
            <div class="form-group col-4">
              <field
                :name="`${fieldsGroupPrefix}pricing.days.${day}.night`"
                layout="empty"
              />
            </div>
          </div>
        </b-card>

        <b-card v-if="id">
          <b-button v-b-modal="'period-pricing-dialog'" class="mr-2">
            {{ $t('vehicles.field_groups.period_pricing') }}
          </b-button>
          <b-button
            v-b-modal="'zone-pricing-dialog'"
            v-if="rideType === 'transfer'"
            class="mr-2"
          >
            {{ $t('vehicles.field_groups.zone_pricing') }}
          </b-button>
          <b-button
            v-b-modal="'airport-rates-dialog'"
            v-if="rideType === 'transfer'"
            class="mr-2"
          >
            {{ $t('vehicles.field_groups.airport-rates') }}
          </b-button>
          <b-button
            class="mr-2"
            @click="$bvModal.show('zone-to-zone-pricing-dialog')"
          >
            {{ $t('vehicles.zone_to_zone_pricing') }}
          </b-button>
          <b-button
            v-b-modal="'availability-zones-dialog'"
            v-if="rideType === 'transfer'"
            class="mr-2"
          >
            {{ $t('vehicles.availability') }}
          </b-button>
          <period-pricing :vehicle-id="id" :ride-type="rideType" />
          <zone-pricing :vehicle-id="id" />
          <airport-rate :vehicle-id="id" />
          <zone-to-zone-pricing :vehicle-id="id" :ride-type="rideType" />
          <availability-zones
            ref="availabilityModal"
            :vehicle-id="id"
            :available-everywhere="model.available_everywhere"
            :ride-type="rideType"
            @changed="availabilityChanged"
          />
        </b-card>
      </b-col>
    </b-row>
    <div class="form-group">
      <b-button type="submit" variant="primary">
        {{ $t('general.save') }}
      </b-button>
      <b-button type="button" variant="light" class="ml-2" @click="cancel">
        {{ $t('general.cancel') }}
      </b-button>
    </div>
  </form>
</template>

<script>
import { ResourceFormMixin } from 'stylemix-vue-admin'
import VehiclesApi from './Api'
import PeriodPricing from './period-pricing/PeriodPricing'
import ZonePricing from './zone-pricing/ZonePricing'
import AirportRate from './airport-rates/AirportRate'
import ZoneToZonePricing from './zone-to-zone-pricing/ZoneToZonePricing'
import AvailabilityZones from './availability-zones/AvailabilityZones'

export default {
  name: 'VehicleForm',

  components: {
    AirportRate,
    AvailabilityZones,
    PeriodPricing,
    ZonePricing,
    ZoneToZonePricing,
  },

  mixins: [ResourceFormMixin],
  props: {
    rideType: {
      type: String,
      default: 'transfer',
    },
  },

  data() {
    return {
      base: 'vehicles',
      days: [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ],
    }
  },

  computed: {
    fieldsGroupPrefix() {
      if (this.rideType === 'transfer') {
        return ''
      }

      return this.rideType + '_'
    },
  },

  mounted() {
    this.loadForm().then(() => {
      this.setFieldGroup('standard', [
        'pricing.by_request',
        'pricing.by_request_percent',
        'pricing.one_way',
        'pricing.per_minute',
        'pricing.price_per_km',
        'pricing.return',
        'pricing.return_price_per_km',
        'pricing.return_discount',
        'pricing.standing_fee',
        'pricing.minimum_price',
        'pricing.stopway_minimum',
        'pricing.prepayment_oncash',
        'pricing.prepayment_percent_oncash',
      ])
      this.setFieldGroup('additional', [
        'pricing.meet_greet',
        'pricing.disabled_place',
        'pricing.baby_seat',
        'pricing.booster_seat',
        'pricing.ski_equipment_fee',
        'pricing.passenger_fee_min',
        'pricing.passenger_fee',
        'pricing.animal_fee',
        'pricing.waiting_time_per_minute',
      ])

      this.setFieldGroup('day_night', [
        'pricing.night_threshold.start',
        'pricing.night_threshold.end',
        'pricing.days_metric',
      ])

      this.setFieldGroup('city_taxi_standard', [
        'city_taxi_pricing.by_request',
        'city_taxi_pricing.by_request_percent',
        'city_taxi_pricing.one_way',
        'city_taxi_pricing.per_minute',
        'city_taxi_pricing.price_per_km',
        'city_taxi_pricing.return',
        'city_taxi_pricing.return_price_per_km',
        'city_taxi_pricing.return_discount',
        'city_taxi_pricing.standing_fee',
        'city_taxi_pricing.minimum_price',
        'city_taxi_pricing.stopway_minimum',
        'city_taxi_pricing.prepayment_oncash',
        'city_taxi_pricing.prepayment_percent_oncash',
      ])
      this.setFieldGroup('city_taxi_additional', [
        'city_taxi_pricing.meet_greet',
        'city_taxi_pricing.disabled_place',
        'city_taxi_pricing.baby_seat',
        'city_taxi_pricing.booster_seat',
        'city_taxi_pricing.ski_equipment_fee',
        'city_taxi_pricing.passenger_fee_min',
        'city_taxi_pricing.passenger_fee',
        'city_taxi_pricing.animal_fee',
        'city_taxi_pricing.waiting_time_per_minute',
      ])

      this.setFieldGroup('city_taxi_day_night', [
        'city_taxi_pricing.night_threshold.start',
        'city_taxi_pricing.night_threshold.end',
        'city_taxi_pricing.days_metric',
      ])

      this.setFieldGroup('delivery_standard', [
        'delivery_pricing.by_request',
        'delivery_pricing.by_request_percent',
        'delivery_pricing.one_way',
        'delivery_pricing.price_per_km',
        'delivery_pricing.stopway_minimum',
        'delivery_pricing.standing_fee',
        'delivery_pricing.minimum_price',
        'delivery_pricing.prepayment_oncash',
        'delivery_pricing.prepayment_percent_oncash',
      ])
      this.setFieldGroup('delivery_additional', [
        'delivery_pricing.door_to_door',
        'delivery_pricing.loaders',
        'delivery_pricing.waiting_time_per_minute',
      ])

      this.setFieldGroup('delivery_day_night', [
        'delivery_pricing.night_threshold.start',
        'delivery_pricing.night_threshold.end',
        'delivery_pricing.days_metric',
      ])

      this.setFieldGroup('ride_types', ['ride_types'])

      this.setFieldGroup('attributes', [
        'attributes.width',
        'attributes.height',
        'attributes.length',
        'attributes.lifting_weight',
        'attributes.pallets',
      ])

      if (!this.model.pricing) {
        this.model.pricing = {}
      }

      if (!this.model.city_taxi_pricing) {
        this.model.city_taxi_pricing = {}
      }
      if (!this.model.delivery_pricing) {
        this.model.delivery_pricing = {}
      }

      if (!this.model.pricing.night_threshold) {
        this.model.pricing.night_threshold = {}
      }
      if (!this.model.city_taxi_pricing.night_threshold) {
        this.model.city_taxi_pricing.night_threshold = {}
      }
      if (!this.model.delivery_pricing.night_threshold) {
        this.model.delivery_pricing.night_threshold = {}
      }
    })
  },

  methods: {
    api() {
      return new VehiclesApi().setParameters({
        division_id: this.currentDivision.id,
      })
    },

    submit() {
      this.save().then(result => {
        this.$toast.success(result.message)
      })
    },

    availabilityChanged() {
      const availability = !this.model.available_everywhere
      this.api()
        .updateAvailability(this.model.id, availability)
        .then(result => {
          this.model.available_everywhere = result.data.available_everywhere
          this.$toast.success(result.message)
        })
    },
  },
}
</script>
